import { calculateEAC, type EacMethod } from '@/lib/utils';
import type { BudgetVsActualEac } from '../types';
import { getEacBadgeText } from './eac-method-labels';
import { mockFinancialsEac } from './forecast-mocks';

export interface BudgetVsActualEacInputs {
  actualCosts: number;
  totalBudget: number;
  actualHours: number;
  budgetHours: number | null | undefined;
  completionPercent: number;
  /** When on, HTC hours are passed as estimateToComplete for the Re-estimate path */
  showHoursToComplete: boolean;
  method: EacMethod;
}

/**
 * EAC costs and hours for the Budget vs Actual table. An estimateToComplete of 0
 * makes calculateEAC fall back to the Simple method.
 */
export function calculateBudgetVsActualEac({
  actualCosts,
  totalBudget,
  actualHours,
  budgetHours,
  completionPercent,
  showHoursToComplete,
  method,
}: BudgetVsActualEacInputs): BudgetVsActualEac {
  const costResult = completionPercent > 0
    ? calculateEAC(
        {
          actualCosts,
          totalBudget,
          completionPercent,
          estimateToComplete: mockFinancialsEac.etc,
        },
        method,
      )
    : null;

  const budgetHoursValue = budgetHours ?? 0;
  const hoursResult = completionPercent > 0 && actualHours > 0 && budgetHoursValue > 0
    ? calculateEAC(
        {
          actualCosts: actualHours,
          totalBudget: budgetHoursValue,
          completionPercent,
          estimateToComplete: showHoursToComplete ? mockFinancialsEac.htcHours : 0,
        },
        method,
      )
    : null;

  const activeMethod = costResult?.method ?? method;

  return {
    eacTotalCosts: costResult?.eac ?? totalBudget,
    eacHours: hoursResult?.eac ?? (budgetHoursValue > 0 ? budgetHoursValue : budgetHours ?? null),
    activeMethod,
    methodBadgeText: getEacBadgeText(activeMethod),
  };
}
